import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchAttendanceHistory, fetchUsersList,
  checkIn, checkOut, fetchTodayAttendance,
  setMyAvailability,
} from '../services/api';
import type { AgentAttendance, User } from '../types';
import { useAuth } from '../context/AuthContext';

// ─── Constants ──────────────────────────────────────────────────────────

const MONTH_NAMES = ['January','February','March','April','May','June',
  'July','August','September','October','November','December'];
const DAY_NAMES = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const STATUS_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  present:  { bg: '#dcfce7', color: '#166534', label: 'Present' },
  absent:   { bg: '#fee2e2', color: '#b91c1c', label: 'Absent' },
  half_day: { bg: '#fef3c7', color: '#92400e', label: 'Half Day' },
};

const AVAILABILITY_OPTIONS: Array<{ value: string; label: string; dot: string }> = [
  { value: 'available', label: 'Available', dot: '#059669' },
  { value: 'busy',      label: 'Busy',      dot: '#d97706' },
  { value: 'away',      label: 'Away',      dot: '#7c3aed' },
  { value: 'offline',   label: 'Offline',   dot: '#6b7280' },
];

// ─── Helpers ────────────────────────────────────────────────────────────

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function firstOfMonth(y: number, m: number): Date { return new Date(y, m, 1); }
function lastOfMonth(y: number, m: number): Date  { return new Date(y, m + 1, 0); }

// ─── Page ───────────────────────────────────────────────────────────────

const AttendancePage: React.FC = () => {
  const { user } = useAuth();
  const canPickUser = user
    ? ['super_admin', 'tenant_admin', 'manager', 'marketing'].includes(user.role)
    : false;

  const now = useMemo(() => new Date(), []);
  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-based

  const [selectedUserId, setSelectedUserId] = useState<number | ''>(
    user?.role === 'agent' ? (user.id ?? '') : ''
  );
  const [agents, setAgents]     = useState<User[]>([]);
  const [rows, setRows]         = useState<AgentAttendance[]>([]);
  const [counts, setCounts]     = useState<{ present: number; absent: number; half_day: number }>({ present: 0, absent: 0, half_day: 0 });
  const [loading, setLoading]   = useState(false);
  const [err, setErr]           = useState<string | null>(null);

  const [todayRow, setTodayRow] = useState<AgentAttendance | null>(null);
  const [checkInBusy, setCheckInBusy] = useState(false);

  // Availability dropdown — mirrors the state of user.availability_status.
  // Optimistic update on change, revert on error.
  const [availability, setAvailability] = useState<string>(user?.availability_status ?? 'available');
  const [availBusy, setAvailBusy] = useState(false);
  useEffect(() => { setAvailability(user?.availability_status ?? 'available'); }, [user?.availability_status]);

  // Load agent list for the admin/manager picker.
  useEffect(() => {
    if (!canPickUser) return;
    fetchUsersList().then(setAgents).catch(() => setAgents([]));
  }, [canPickUser]);

  // Load calendar data for the current month + selected user.
  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const from = isoDate(firstOfMonth(year, month));
      const to   = isoDate(lastOfMonth(year, month));
      const uid  = typeof selectedUserId === 'number' ? selectedUserId : undefined;
      const res = await fetchAttendanceHistory(from, to, uid);
      setRows(res.rows ?? []);
      setCounts(res.counts ?? { present: 0, absent: 0, half_day: 0 });
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? 'Failed to load attendance');
      setRows([]);
    } finally { setLoading(false); }
  }, [year, month, selectedUserId]);

  useEffect(() => { load(); }, [load]);

  // Load today's row for the check-in / check-out card (agent view only).
  useEffect(() => {
    if (user?.role !== 'agent') return;
    fetchTodayAttendance()
      .then((res: AgentAttendance[]) => setTodayRow(res.length > 0 ? res[0] : null))
      .catch(() => setTodayRow(null));
  }, [user]);

  const doCheckIn = async () => {
    setCheckInBusy(true);
    try { await checkIn(); load(); const t = await fetchTodayAttendance(); setTodayRow(t.length > 0 ? t[0] : null); }
    catch (e: any) { alert('Check-in failed: ' + (e?.response?.data?.error ?? e?.message)); }
    finally { setCheckInBusy(false); }
  };
  const doCheckOut = async () => {
    setCheckInBusy(true);
    try { await checkOut(); load(); const t = await fetchTodayAttendance(); setTodayRow(t.length > 0 ? t[0] : null); }
    catch (e: any) { alert('Check-out failed: ' + (e?.response?.data?.error ?? e?.message)); }
    finally { setCheckInBusy(false); }
  };

  const changeAvailability = async (next: string) => {
    const prev = availability;
    setAvailability(next); // optimistic
    setAvailBusy(true);
    try { await setMyAvailability(next); }
    catch (e: any) {
      setAvailability(prev); // revert on failure
      alert('Availability change failed: ' + (e?.response?.data?.error ?? e?.message));
    }
    finally { setAvailBusy(false); }
  };

  // Build a map date → row for O(1) calendar cell lookup.
  const byDate = useMemo(() => {
    const map: Record<string, AgentAttendance> = {};
    for (const r of rows) map[r.date] = r;
    return map;
  }, [rows]);

  const first    = firstOfMonth(year, month).getDay();
  const numDays  = lastOfMonth(year, month).getDate();
  const todayIso = isoDate(now);

  const changeMonth = (delta: number) => {
    let m = month + delta;
    let y = year;
    if (m < 0)  { m = 11; y--; }
    if (m > 11) { m = 0;  y++; }
    setMonth(m); setYear(y);
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '20px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Attendance</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            {user?.role === 'agent'
              ? 'Your attendance calendar and check-in / check-out.'
              : 'Team attendance calendar. Pick an agent to see their record.'}
          </p>
        </div>
        {canPickUser && (
          <div>
            <select
              value={selectedUserId}
              onChange={e => setSelectedUserId(e.target.value ? Number(e.target.value) : '')}
              style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, minWidth: 200 }}
            >
              <option value="">Whole team</option>
              {agents.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Agent: today check-in / check-out card + availability dropdown */}
      {user?.role === 'agent' && (
        <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Today</div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>
              {todayRow?.checked_in_at
                ? <>Checked in at {new Date(todayRow.checked_in_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                  {todayRow.checked_out_at && <> · Checked out at {new Date(todayRow.checked_out_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</>}
                </>
                : 'Not checked in yet.'}
            </div>
          </div>
          {!todayRow?.checked_in_at && (
            <button className="btn" onClick={doCheckIn} disabled={checkInBusy}
              style={{ padding: '8px 14px', fontSize: 13, background: '#059669', color: 'white', border: 'none', borderRadius: 6 }}>
              {checkInBusy ? '…' : 'Check In'}
            </button>
          )}
          {todayRow?.checked_in_at && !todayRow?.checked_out_at && (
            <button className="btn" onClick={doCheckOut} disabled={checkInBusy}
              style={{ padding: '8px 14px', fontSize: 13, background: '#dc2626', color: 'white', border: 'none', borderRadius: 6 }}>
              {checkInBusy ? '…' : 'Check Out'}
            </button>
          )}

          {/* Availability picker — always visible so agent can toggle
              busy/away/offline even without checking in first. Feeds the
              auto-assignment eligibility filter server-side. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Availability:</span>
            <span
              aria-hidden
              style={{
                width: 10, height: 10, borderRadius: '50%',
                background: AVAILABILITY_OPTIONS.find(o => o.value === availability)?.dot ?? '#6b7280',
                display: 'inline-block',
              }}
            />
            <select
              value={availability}
              onChange={e => changeAvailability(e.target.value)}
              disabled={availBusy}
              style={{
                padding: '6px 10px', fontSize: 13,
                border: '1px solid var(--border)', borderRadius: 6,
                background: '#fff',
              }}
            >
              {AVAILABILITY_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Summary counts */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        <StatCard label="Present"  value={counts.present}  color="#059669" bg="#dcfce7" />
        <StatCard label="Absent"   value={counts.absent}   color="#b91c1c" bg="#fee2e2" />
        <StatCard label="Half Day" value={counts.half_day} color="#92400e" bg="#fef3c7" />
        <StatCard label="Total marked" value={counts.present + counts.absent + counts.half_day} color="var(--text)" bg="var(--bg-alt)" />
      </div>

      {/* Calendar card */}
      <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <button onClick={() => changeMonth(-1)} className="btn"
            style={{ padding: '6px 12px', fontSize: 13, background: 'transparent', border: '1px solid var(--border)', borderRadius: 6 }}>
            ← Prev
          </button>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{MONTH_NAMES[month]} {year}</div>
          <button onClick={() => changeMonth(+1)} className="btn"
            style={{ padding: '6px 12px', fontSize: 13, background: 'transparent', border: '1px solid var(--border)', borderRadius: 6 }}>
            Next →
          </button>
        </div>

        {err && <div style={{ color: '#dc2626', marginBottom: 10, fontSize: 13 }}>{err}</div>}
        {loading && <div style={{ color: 'var(--text-muted)', marginBottom: 10, fontSize: 13 }}>Loading…</div>}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
          {DAY_NAMES.map(d => (
            <div key={d} style={{ padding: '6px 0', textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' }}>
              {d}
            </div>
          ))}
          {Array.from({ length: first }).map((_, i) => <div key={`e-${i}`} />)}
          {Array.from({ length: numDays }).map((_, i) => {
            const day  = i + 1;
            const iso  = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const row  = byDate[iso];
            const isToday = iso === todayIso;
            const style  = row ? STATUS_STYLE[row.status] : null;

            return (
              <div key={day}
                title={row
                  ? `${style?.label}${row.checked_in_at ? ' · in ' + new Date(row.checked_in_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}${row.checked_out_at ? ' · out ' + new Date(row.checked_out_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}${row.notes ? '\n' + row.notes : ''}`
                  : 'No record'}
                style={{
                  minHeight: 60,
                  padding: 8,
                  borderRadius: 8,
                  background: style?.bg ?? 'var(--bg-alt)',
                  color:      style?.color ?? 'var(--text-muted)',
                  border: isToday ? '2px solid var(--primary)' : '1px solid transparent',
                  fontSize: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 13 }}>{day}</div>
                <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase' }}>
                  {style?.label ?? ''}
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 16, marginTop: 14, fontSize: 12, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
          {Object.entries(STATUS_STYLE).map(([k, s]) => (
            <div key={k} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ width: 14, height: 14, borderRadius: 3, background: s.bg, display: 'inline-block' }} />
              {s.label}
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ width: 14, height: 14, borderRadius: 3, background: 'var(--bg-alt)', border: '1px solid var(--border)', display: 'inline-block' }} />
            No record
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: number; color: string; bg: string }> = ({ label, value, color, bg }) => (
  <div style={{ background: bg, borderRadius: 10, padding: '14px 16px' }}>
    <div style={{ fontSize: 11, fontWeight: 700, color, opacity: 0.85, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
  </div>
);

export default AttendancePage;
